/* ==========================================================================
   Charm Optical — search core (pure functions, no DOM).
   Loaded before search-overlay.js; also runs in Node for tests
   (scripts/test-search-core.mjs) so the matching rules are provable offline.

   Index shape (assets/search-index.json, built by scripts/build-search-index.mjs):
     [version, [[handle, title, vendor, type, img, price, codes], ...]]
   `codes` = space-joined lowercase model tokens: "rb4461d 4461 0rb4461d".

   Why this exists (2026-09-03): Shopify search only matches the START of a
   whole word, so "4515" never finds "Versace VE4515" and "2140" finds nothing.
   Customers and staff type the number they see on the temple. Here a
   model-number-looking query is matched against the product's codes with a
   strict ranking (exact → digits exact → prefix → substring), for every
   product in the store, in a few milliseconds.
   ========================================================================== */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.CharmSearchCore = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  function normalize(s) {
    var str = String(s == null ? '' : s).toLowerCase();
    if (str.normalize) str = str.normalize('NFD').replace(/[̀-ͯ]/g, '');
    return str;
  }
  function norm(s) {
    return normalize(s).replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
  }
  // letters+digits only: "VE 4515" → "ve4515", "0RB-4461D" → "0rb4461d"
  function squash(s) {
    return normalize(s).replace(/[^a-z0-9]+/g, '');
  }

  // A query that reads like a model number: optional brand letters, 2–5 digits,
  // optional suffix. "4515", "rb2140", "ve 4515", "0ve4515", "4461d", "17ws".
  var CODE_Q = /^[a-z]{0,4}\d{2,5}[a-z]{0,3}$/;
  function codeQuery(q) {
    var s = squash(q).replace(/^0(?=[a-z])/, '');   // Luxottica tag/SKU form "0VE4515" → "ve4515"
    return (s.length >= 2 && CODE_Q.test(s)) ? s : '';
  }

  function prepare(rows) {
    var out = new Array(rows.length);
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      var title = r[1] || '';
      out[i] = {
        h: r[0], t: title, v: r[2] || '', y: r[3] || '', i: r[4] || '', p: r[5] || '',
        codes: r[6] ? String(r[6]).split(' ') : [],
        st: squash(title),
        nh: norm(title + ' ' + (r[2] || '') + ' ' + (r[3] || ''))
      };
    }
    return out;
  }

  function digitsOf(s) { var m = String(s).match(/\d+/); return m ? m[0] : ''; }

  // Lower = better. null = no match.
  //   0   exact code            ("ve4515" = ve4515, "4515" = the bare-digits token)
  //   1   digits exact          ("4515" vs ve4515)
  //   1.5 code prefix           ("rb44" → rb4461d)
  //   2   digits prefix         ("451"  → 4515)
  //   2.5 code substring        ("461d" → rb4461d)
  //   3   digits substring
  //   4   squashed-title substring (last resort)
  // Returns { score, code } for the best-matching code, or null.
  function codeMatch(e, sq) {
    var onlyDigits = /^\d+$/.test(sq);
    var best = null, bestCode = '';
    for (var k = 0; k < e.codes.length; k++) {
      var c = e.codes[k], s = null;
      if (c === sq) s = 0;
      else if (onlyDigits) {
        var d = digitsOf(c);
        if (!d) continue;
        if (d === sq) s = 1;
        else if (d.indexOf(sq) === 0) s = 2;
        else if (d.indexOf(sq) !== -1) s = 3;
      } else if (c.indexOf(sq) === 0) s = 1.5;
      else if (c.indexOf(sq) !== -1) s = 2.5;
      if (s !== null && (best === null || s < best)) { best = s; bestCode = c; }
    }
    if (best === null && sq.length >= 3 && e.st.indexOf(sq) !== -1) { best = 4; bestCode = ''; }
    return best === null ? null : { score: best, code: bestCode };
  }
  function codeScore(e, sq) { var m = codeMatch(e, sq); return m ? m.score : null; }

  // The lettered form of a product's best code ("rx5433", not "5433" / "0rx5433"),
  // for display and for the See-all link.
  function displayCode(e, sq) {
    var m = codeMatch(e, sq);
    if (!m) return '';
    var d = digitsOf(m.code);
    for (var k = 0; k < e.codes.length; k++) {
      var c = e.codes[k];
      if (/^[a-z]{2,}\d/.test(c) && (!d || digitsOf(c) === d)) return c.toUpperCase();
    }
    return m.code ? m.code.toUpperCase() : '';
  }

  function codeSearch(entries, sq, limit) {
    var hits = [];
    for (var i = 0; i < entries.length; i++) {
      var m = codeMatch(entries[i], sq);
      if (m) hits.push({ entry: entries[i], score: m.score, code: m.code, num: parseInt(digitsOf(m.code) || '0', 10) });
    }
    // Ties (e.g. every RX543x on "543") sort by model NUMBER, then code, then title —
    // the order a person reading the temple would expect, not alphabetical by name.
    hits.sort(function (a, b) {
      return a.score - b.score || a.num - b.num ||
        (a.code < b.code ? -1 : a.code > b.code ? 1 : 0) ||
        (a.entry.t < b.entry.t ? -1 : a.entry.t > b.entry.t ? 1 : 0);
    });
    return limit ? hits.slice(0, limit) : hits;
  }

  // Shopify's own /search page understands "A OR B". For a number-only query
  // hand it the lettered model codes we matched so "See all results" works too.
  function seeAllQuery(hits, max) {
    var seen = {}, out = [];
    for (var i = 0; i < hits.length && out.length < (max || 4); i++) {
      if (hits[i].score > 3) continue;
      var codes = hits[i].entry.codes;
      for (var k = 0; k < codes.length; k++) {
        var c = codes[k];
        if (!/^[a-z]{2,}\d/.test(c)) continue;          // lettered code only (rb2140, not 2140 / 0rb2140)
        if (seen[c]) break;
        seen[c] = 1; out.push(c.toUpperCase()); break;
      }
    }
    return out.join(' OR ');
  }

  // bounded Levenshtein (early-exit at maxD)
  function editDistance(a, b, maxD) {
    if (a === b) return 0;
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > maxD) return maxD + 1;
    var prev = [], cur = [], i, j;
    for (j = 0; j <= lb; j++) prev[j] = j;
    for (i = 1; i <= la; i++) {
      cur[0] = i;
      var rowMin = cur[0];
      for (j = 1; j <= lb; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (cur[j] < rowMin) rowMin = cur[j];
      }
      if (rowMin > maxD) return maxD + 1;
      var t = prev; prev = cur; cur = t;
    }
    return prev[lb];
  }

  // Free-text scoring (title/vendor/type), typo tolerant. nq = normalized query.
  function textScore(e, nq, tokens) {
    var hay = e.nh, title = norm(e.t);
    if (title.indexOf(nq) === 0) return 0;              // prefix
    if (title.indexOf(nq) !== -1) return 1;             // substring
    if (hay.indexOf(nq) !== -1) return 2;               // vendor/type substring
    var covered = 0, fuzzy = 0;
    for (var i = 0; i < tokens.length; i++) {
      var tk = tokens[i];
      if (tk.length < 2) { covered++; continue; }
      if (hay.indexOf(tk) !== -1) { covered++; continue; }
      var words = hay.split(' ');
      var hit = false;
      var maxD = tk.length <= 4 ? 1 : (tk.length <= 7 ? 2 : 3);
      for (var w = 0; w < words.length; w++) {
        if (!words[w]) continue;
        // containment shortcuts need real words: a 1–2 letter word ("i" in
        // "I-DROP", "rx") used to match any token containing that letter.
        if (tk.length >= 3 && words[w].indexOf(tk) !== -1) { hit = true; break; }
        if (words[w].length >= 3 && tk.indexOf(words[w]) !== -1) { hit = true; break; }
        if (editDistance(tk, words[w], maxD) <= maxD) { hit = true; fuzzy++; break; }
      }
      if (hit) covered++;
    }
    if (covered < tokens.length) return null;
    return 3 + fuzzy;
  }

  function textSearch(entries, nq, limit) {
    if (!nq) return [];
    var tokens = nq.split(' ').filter(Boolean);
    var hits = [];
    for (var i = 0; i < entries.length; i++) {
      var s = textScore(entries[i], nq, tokens);
      if (s !== null) hits.push({ entry: entries[i], score: s });
    }
    hits.sort(function (a, b) { return a.score - b.score; });
    return limit ? hits.slice(0, limit) : hits;
  }

  return {
    normalize: normalize, norm: norm, squash: squash,
    codeQuery: codeQuery, prepare: prepare,
    codeScore: codeScore, codeMatch: codeMatch, displayCode: displayCode, codeSearch: codeSearch, seeAllQuery: seeAllQuery,
    editDistance: editDistance, textScore: textScore, textSearch: textSearch
  };
});
